/**
 * Image Quality Validation Utilities
 * Check image quality before uploading for better OCR results
 */

/**
 * Validate image quality
 * @param {File} file - The image file to validate
 * @returns {Promise<{valid: boolean, warnings: string[]}>}
 */
export async function validateImageQuality(file) {
  const warnings = [];
  
  // Check file size
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > 10) {
    warnings.push(`File size is ${fileSizeMB.toFixed(1)}MB (max 10MB recommended)`);
  }
  
  if (fileSizeMB < 0.1) {
    warnings.push('File size is very small - image quality may be poor');
  }
  
  // Check if it's an image
  if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
    warnings.push('Only images (JPG, PNG) and PDF files are supported');
    return { valid: false, warnings };
  }
  
  // Skip further checks for PDFs
  if (file.type === 'application/pdf') {
    return { valid: true, warnings };
  }
  
  try {
    // Load image for analysis
    const imageData = await loadImage(file);
    
    // Check dimensions
    if (imageData.width < 600 || imageData.height < 600) {
      warnings.push(`Image resolution is low (${imageData.width}x${imageData.height}). Minimum 600x600 recommended for clear text.`);
    }
    
    if (imageData.width > 4000 || imageData.height > 4000) {
      warnings.push(`Image resolution is very high (${imageData.width}x${imageData.height}). Consider resizing to improve upload speed.`);
    }
    
    // Check aspect ratio (detect if rotated incorrectly)
    const aspectRatio = imageData.width / imageData.height;
    if (aspectRatio < 0.5 || aspectRatio > 2) {
      warnings.push('⚠️ Unusual aspect ratio detected - image may need rotation');
    }
    
    // Detect if image is likely rotated 90 degrees (portrait when should be landscape)
    if (imageData.width < imageData.height && aspectRatio < 0.7) {
      warnings.push('🔄 Image appears to be in portrait orientation - consider rotating for better text recognition');
    }
    
    // Detect very wide or very tall images
    if (aspectRatio > 1.5 && imageData.height < 800) {
      warnings.push('📐 Wide image with low height - ensure all text is clearly visible');
    }
    
    // Check brightness and blur
    const quality = analyzeImageQuality(imageData.canvas);
    
    if (quality.brightness < 60) {
      warnings.push('🌑 Image is very dark - use better lighting or adjust brightness before uploading');
    } else if (quality.brightness < 80) {
      warnings.push('🔆 Image appears dark - may affect text recognition quality');
    }
    
    if (quality.brightness > 240) {
      warnings.push('☀️ Image is overexposed (too bright) - reduce brightness or avoid direct flash');
    } else if (quality.brightness > 220) {
      warnings.push('💡 Image is very bright - may wash out text details');
    }
    
    if (quality.blur > 0.8) {
      warnings.push('⚠️ Image is significantly blurred - retake photo with steady hand or use tripod');
    } else if (quality.blur > 0.7) {
      warnings.push('📷 Image appears slightly blurry - consider retaking for better accuracy');
    }
    
    // Low quality combination warning
    if (quality.blur > 0.7 && (quality.brightness < 80 || quality.brightness > 220)) {
      warnings.push('❌ CRITICAL: Image has both blur and lighting issues - strongly recommend retaking photo');
    }
    
  } catch (error) {
    console.error('Image analysis error:', error);
    warnings.push('Could not analyze image quality');
  }
  
  return {
    valid: warnings.length === 0 || !warnings.some(w => w.includes('not supported')),
    warnings
  };
}

/**
 * Load image from file
 */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        resolve({
          width: img.width,
          height: img.height,
          canvas: canvas
        });
      };
      
      img.onerror = reject;
      img.src = e.target.result;
    };
    
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Analyze image quality (brightness and blur)
 */
function analyzeImageQuality(canvas) {
  const ctx = canvas.getContext('2d');
  const width = Math.min(canvas.width, 500); // Sample smaller area for performance
  const height = Math.min(canvas.height, 500);
  
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  let totalBrightness = 0;
  let blurScore = 0;
  
  // Calculate average brightness
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    totalBrightness += (r + g + b) / 3;
  }
  
  const avgBrightness = totalBrightness / (data.length / 4);
  
  // Simple blur detection using edge detection
  // Calculate variance of pixel differences
  let variance = 0;
  for (let i = 0; i < data.length - 4; i += 4) {
    const diff = Math.abs(data[i] - data[i + 4]);
    variance += diff;
  }
  
  const avgVariance = variance / (data.length / 4);
  
  // Normalize blur score (higher = more blur, lower = sharper)
  // Sharp images have high variance in adjacent pixels
  blurScore = 1 - Math.min(avgVariance / 50, 1);
  
  return {
    brightness: avgBrightness,
    blur: blurScore
  };
}

/**
 * Validate ZIP file
 */
export function validateZipFile(file) {
  const warnings = [];
  
  if (!file.name.endsWith('.zip')) {
    warnings.push('Only .zip files are supported');
    return { valid: false, warnings };
  }
  
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > 100) {
    warnings.push(`ZIP file is ${fileSizeMB.toFixed(1)}MB (max 100MB allowed)`);
    return { valid: false, warnings };
  }
  
  return { valid: true, warnings };
}
